import type { DetrixSession } from '../types';
import { filterUnifiedNetworkRows, mergeUnifiedNetworkRows } from './networkRowFilter';

/** Map session time to horizontal percent within the visible window [viewStart, viewEnd]. */
export function pctInView(viewStart: number, viewEnd: number, t: number): number {
  const span = Math.max(1e-9, viewEnd - viewStart);
  return ((t - viewStart) / span) * 100;
}

/** Fraction 0..1 along the scrubber → time. */
export function timeFromFrac(viewStart: number, viewEnd: number, frac01: number): number {
  const span = viewEnd - viewStart;
  return viewStart + frac01 * span;
}

export function clampViewToSession(
  tMin: number,
  tMax: number,
  viewStart: number,
  viewEnd: number,
): { start: number; end: number } {
  const d = Math.max(0, tMax - tMin);
  if (d <= 0) return { start: tMin, end: tMax };
  let span = viewEnd - viewStart;
  if (span >= d - 1e-9) {
    return { start: tMin, end: tMax };
  }
  span = Math.max(span, 1e-6);
  let start = viewStart;
  let end = viewEnd;
  if (start < tMin) {
    start = tMin;
    end = tMin + span;
  }
  if (end > tMax) {
    end = tMax;
    start = tMax - span;
  }
  if (start < tMin) start = tMin;
  return { start, end };
}

/** factor &lt; 1 zooms in (narrower span), factor &gt; 1 zooms out. anchorT is the time under the cursor / playhead. */
export function zoomAroundAnchor(
  tMin: number,
  tMax: number,
  viewStart: number,
  viewEnd: number,
  factor: number,
  anchorT: number,
  minSpanRatio = 1 / 600,
): { start: number; end: number } {
  const d = Math.max(1e-9, tMax - tMin);
  let span = Math.max(1e-9, viewEnd - viewStart);
  const anchor = Math.min(Math.max(anchorT, tMin), tMax);
  const spanFrac = (anchor - viewStart) / span;
  const minSpan = Math.max(d * minSpanRatio, 8);
  span = Math.min(d, Math.max(minSpan, span * factor));
  const start = anchor - spanFrac * span;
  const end = start + span;
  return clampViewToSession(tMin, tMax, start, end);
}

export function panViewByMs(
  tMin: number,
  tMax: number,
  viewStart: number,
  viewEnd: number,
  deltaMs: number,
): { start: number; end: number } {
  return clampViewToSession(tMin, tMax, viewStart + deltaMs, viewEnd + deltaMs);
}

/** Pick a “nice” tick step in ms (1–2–5 × 10^n style). */
export function niceTickStepMs(roughStepMs: number): number {
  if (!Number.isFinite(roughStepMs) || roughStepMs <= 0) return 1;
  const exp = Math.floor(Math.log10(roughStepMs));
  const base = 10 ** exp;
  const frac = roughStepMs / base;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * base;
}

export type RulerTick = { t: number; pct: number; major: boolean };

export function buildRulerTicks(
  viewStart: number,
  viewEnd: number,
  targetMajorTicks: number,
): RulerTick[] {
  const span = Math.max(1e-9, viewEnd - viewStart);
  const rough = span / Math.max(2, targetMajorTicks);
  const majorStep = niceTickStepMs(rough);
  const ticks: RulerTick[] = [];
  const firstMajor = Math.ceil(viewStart / majorStep) * majorStep;
  for (let t = firstMajor; t <= viewEnd + 1e-6; t += majorStep) {
    ticks.push({ t, pct: pctInView(viewStart, viewEnd, t), major: true });
  }
  return ticks;
}

export function buildSnapTimes(session: DetrixSession, tMin: number, tMax: number): number[] {
  const set = new Set<number>();
  const add = (x: number) => {
    if (Number.isFinite(x) && x >= tMin && x <= tMax) set.add(Math.round(x * 1000) / 1000);
  };
  for (const s of session.steps ?? []) add(s.timestampStart);
  for (const a of session.actions ?? []) add(a.timestamp);
  for (const n of session.network ?? []) add(n.timestamp);
  for (const r of session.browserResources ?? []) add(r.timestamp);
  for (const e of session.events ?? []) add(e.timestamp);
  return [...set].sort((a, b) => a - b);
}

export function nearestSnapTime(sortedTimes: number[], target: number, thresholdMs: number): number | null {
  if (sortedTimes.length === 0 || thresholdMs <= 0) return null;
  let lo = 0;
  let hi = sortedTimes.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sortedTimes[mid]! < target) lo = mid + 1;
    else hi = mid;
  }
  let best: number | null = null;
  let bestD = thresholdMs + 1;
  const tryT = (t: number | undefined) => {
    if (t === undefined) return;
    const d = Math.abs(t - target);
    if (d <= thresholdMs && d < bestD) {
      bestD = d;
      best = t;
    }
  };
  tryT(sortedTimes[lo]!);
  tryT(sortedTimes[lo - 1]!);
  return best;
}

export type HoverSample = { t: number; lines: string[] };

function shortUrl(url: string, max = 28): string {
  try {
    const u = new URL(url);
    const s = (u.pathname + u.search).replace(/^\//, '') || u.host;
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  } catch {
    return url.length > max ? `${url.slice(0, max - 1)}…` : url;
  }
}

/** Flat timeline samples for ruler hover (sorted by t). */
export function buildTimelineHoverSamples(
  session: DetrixSession,
  tMin: number,
  tMax: number,
  networkUrlQuery: string,
  networkTypeTags: readonly string[],
): HoverSample[] {
  const out: HoverSample[] = [];
  const push = (t: number, lines: string[]) => {
    if (!Number.isFinite(t) || t < tMin || t > tMax) return;
    out.push({ t: Math.round(t * 1000) / 1000, lines });
  };
  for (const s of session.steps ?? []) {
    push(s.timestampStart, ['Step', s.title]);
  }
  for (const a of session.actions ?? []) {
    push(a.timestamp, ['Action', a.summary]);
  }
  const netMerged = mergeUnifiedNetworkRows(session.network ?? [], session.browserResources ?? []);
  const netRows = filterUnifiedNetworkRows(netMerged, networkUrlQuery, networkTypeTags);
  for (const row of netRows) {
    if (row.kind === 'network') {
      const n = row.entry;
      const st = n.response?.status ?? 0;
      push(n.timestamp, [`${n.request.method} ${shortUrl(n.request.url)}`, st ? `→ ${st}` : '']);
    } else {
      const r = row.entry;
      push(r.timestamp, [`Resource (${r.resourceType})`, shortUrl(r.url)]);
    }
  }
  for (const e of session.events ?? []) {
    const err = e.type === 'error' || e.type === 'system.error';
    push(e.timestamp, err ? ['Console / event', e.type] : ['Event', e.type]);
  }
  out.sort((a, b) => a.t - b.t);
  return out;
}

export function nearestHoverSample(samples: HoverSample[], t: number): HoverSample | null {
  if (samples.length === 0) return null;
  let lo = 0;
  let hi = samples.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (samples[mid]!.t < t) lo = mid + 1;
    else hi = mid;
  }
  const i = lo;
  const cur = samples[i]!;
  const prev = samples[i - 1];
  if (!prev) return cur;
  return Math.abs(prev.t - t) <= Math.abs(cur.t - t) ? prev : cur;
}
